import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { adminApi } from '../../../features/admin/admin.api';
import { useAuth } from '../../../features/auth/auth-context';
import { toDisplayMessage } from '../../../lib/error-message';
import { paths, routeTo } from '../../../routes/paths';
import styles from './AdminPortal.module.css';

export function AdminUsersPage(): React.JSX.Element {
  const { can } = useAuth();
  const [emailFilter, setEmailFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [page, setPage] = useState(1);

  const users = useQuery({
    queryKey: ['admin', 'users', { page, email: appliedFilter }],
    queryFn: () => adminApi.listUsers({ page, pageSize: 20, email: appliedFilter }),
  });

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Users</h1>

        {can(PERMISSIONS.USERS_CREATE) && (
          <Link to={paths.adminUserCreate}>
            <Button>Create staff user</Button>
          </Link>
        )}
      </header>

      <section className={styles.card} aria-labelledby="filters-heading">
        <h2 className={styles.cardTitle} id="filters-heading">
          Filter
        </h2>

        <form
          className={styles.filters}
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setAppliedFilter(emailFilter);
          }}
        >
          <FormField
            label="Email contains"
            type="search"
            name="email"
            value={emailFilter}
            onChange={(event) => {
              setEmailFilter(event.target.value);
            }}
          />
          <Button type="submit">Apply</Button>
        </form>
      </section>

      <section className={styles.card} aria-labelledby="users-heading">
        <h2 className={styles.cardTitle} id="users-heading">
          Accounts
        </h2>

        {users.isPending && <PageSpinner label="Loading users" />}
        {users.isError && <Alert tone="error">{toDisplayMessage(users.error)}</Alert>}

        {users.isSuccess && users.data.items.length === 0 && (
          <p className={styles.empty}>
            No staff users match this filter. Clear the email filter to widen the list.
          </p>
        )}

        {users.isSuccess && users.data.items.length > 0 && (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <caption>
                  {users.data.pagination.totalItems} accounts, page {users.data.pagination.page} of{' '}
                  {users.data.pagination.totalPages}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Email</th>
                    <th scope="col">Type</th>
                    <th scope="col">Status</th>
                    <th scope="col">Roles</th>
                    <th scope="col">Last sign-in</th>
                  </tr>
                </thead>
                <tbody>
                  {users.data.items.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <Link to={routeTo.adminUser(user.id)}>{user.email}</Link>
                      </td>
                      <td>{user.userType}</td>
                      <td>
                        <StatusBadge status={user.status} />
                      </td>
                      <td>{user.roles.map((role) => role.code).join(', ') || '—'}</td>
                      <td>
                        {user.lastLoginAt === null
                          ? 'Never'
                          : new Date(user.lastLoginAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <nav className={styles.pagination} aria-label="Pagination">
              <Button
                variant="secondary"
                disabled={!users.data.pagination.hasPreviousPage}
                onClick={() => {
                  setPage((current) => current - 1);
                }}
              >
                Previous
              </Button>
              <span>
                Page {users.data.pagination.page} of {users.data.pagination.totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={!users.data.pagination.hasNextPage}
                onClick={() => {
                  setPage((current) => current + 1);
                }}
              >
                Next
              </Button>
            </nav>
          </>
        )}
      </section>
    </div>
  );
}
